import { getDependencies } from "@/libs/cv/modules/illumination/dependencies"
import { throttleMark, type PerformanceEntry } from "@/libs/telemetry/performance"
import { getFetchEntriesMeasurements } from "@/libs/telemetry/performance/fetch-entries"
import type { Graph } from "../../framework"
import type { FramePixels, Landmarks } from "../types.d"
import type { Dependencies, Output as Illumination, Input } from "./illumination.model"
import {
  INPUT_TYPE,
  OUTPUT_TYPE,
  type DataInput,
  type InitInput,
  type Output,
  type ResultOutput,
} from "./illumination.worker"
import IlluminationWorker from "./illumination.worker.ts?worker&inline"

export type { Illumination }

export type CreateOptions = {
  performance?: PerformanceEntry
  initialState?: Partial<Illumination>
  onProgress?: (progress: number) => void
}

export const defaultIllumination: Illumination = {
  intensity: 0,
  azimuth: 0,
  elevation: 0,
}

export const create = async <I extends unknown[], O extends unknown[]>(
  graph: Graph<I, O>,
  opts?: CreateOptions,
) => {
  /**
   * Internal node‘s state.
   * In case there is no face detection, the result of the last light intensity calculation will be returned.
   */
  const nodeState: { isReady: boolean; illumination: Illumination } = {
    isReady: false,
    illumination: Object.assign(defaultIllumination, opts?.initialState),
  }
  const loadingPerf = opts?.performance?.mark("cv:onnx-loading")

  const estimator = await createEstimator({
    onProgress: opts?.onProgress,
    onReady: async (deps) => {
      const fetchMeasurements = await getFetchEntriesMeasurements((entry) =>
        deps.some((asset) => entry.name.includes(asset.url)),
      )

      loadingPerf?.measure({ children: fetchMeasurements })
      nodeState.isReady = true
    },
  })

  const inferenceMark = throttleMark(opts?.performance, 1000 / 2)

  return graph.node<
    [frame: FramePixels, faceMesh: { landmarks: Landmarks } | null],
    [Illumination]
  >({
    process: async (ctx) => {
      const frame = ctx.in(0)
      const faceMesh = ctx.in(1)
      // Skip detached frames
      if (nodeState.isReady && faceMesh && frame.data.byteLength > 0) {
        const inferencePerf = inferenceMark("cv:illumination-inference")
        nodeState.illumination = await estimator.predict(frame, faceMesh.landmarks)
        inferencePerf?.measure()
      }

      ctx.out(0, nodeState.illumination)
    },
    destroy: () => estimator.destroy(),
  })
}

const getROI = (landmarks: Landmarks) => {
  const top = landmarks[10].y
  const bottom = landmarks[0].y
  const left = Math.min(landmarks[127].x, landmarks[24].x, landmarks[143].x)
  const right = Math.max(landmarks[264].x, landmarks[356].x, landmarks[372].x)

  return [top, left, 0, bottom, right, 1]
}

const frameToInput = (frame: FramePixels, landmarks: Landmarks): Input => ({
  frame,
  roi: getROI(landmarks),
})

const createEstimator = async ({
  onProgress,
  onReady,
}: {
  onProgress?: (progress: number) => void
  onReady: (deps: Dependencies) => void
}) => {
  /**
   * A workaround to make worker work
   * Vite incorrectly imports dependencies urls in getDependencies function in worker environment imported as &inline
   * It builds URL using self.location.href as base, and inside worker it's equal to `blob:...`, which makes it invalid
   * So we need to import dependencies before worker creation and pass them as a message
   */
  const deps = await getDependencies()

  try {
    // Wait for worker to be ready
    const worker = await new Promise<Worker>((resolve, reject) => {
      const worker = new IlluminationWorker()
      const abortController = new AbortController()

      worker.addEventListener(
        "message",
        (e: MessageEvent<Output>) => {
          if (e.data.type === OUTPUT_TYPE.PROGRESS) onProgress?.(e.data.progress)
          else if (e.data.type === OUTPUT_TYPE.READY) {
            abortController.abort()
            resolve(worker)
          }
        },
        { signal: abortController.signal },
      )
      worker.addEventListener("error", (e) => {
        abortController.abort()
        reject(e)
      })

      worker.postMessage({ type: INPUT_TYPE.INIT, dependencies: deps } satisfies InitInput)
    })

    const operations = new Map<number, (data: ResultOutput["result"]) => void>()
    const abortController = new AbortController()

    worker.addEventListener(
      "message",
      (e: MessageEvent<Output>) => {
        if (e.data.type !== OUTPUT_TYPE.RESULT) return

        operations.get(e.data.timestamp)?.(e.data.result)
        operations.delete(e.data.timestamp)
      },
      { signal: abortController.signal },
    )

    const predict = (frame: FramePixels, landmarks: Landmarks) =>
      new Promise<Illumination>((resolve, reject) => {
        const timestamp = Date.now()

        worker.addEventListener("error", reject, { once: true, signal: abortController.signal })

        operations.set(timestamp, (data) => {
          resolve(data)
          worker.removeEventListener("error", reject)
        })

        worker.postMessage(
          {
            type: INPUT_TYPE.DATA,
            ...frameToInput(frame, landmarks),
            timestamp,
          } satisfies DataInput,
          [frame.data.buffer],
        )
      })

    onReady(deps)

    return {
      predict,
      destroy: async () => {
        await new Promise<void>((resolve, reject) => {
          worker.addEventListener(
            "message",
            (e: MessageEvent<Output>) => {
              if (e.data.type !== OUTPUT_TYPE.DESTROYED) return

              worker.removeEventListener("error", reject)
              resolve()
            },
            { signal: abortController.signal },
          )
          worker.addEventListener(
            "error",
            (e) => {
              abortController.abort()
              reject(e)
            },
            { once: true, signal: abortController.signal },
          )

          worker.postMessage({ type: INPUT_TYPE.DESTROY })
        })

        abortController.abort()
        worker.terminate()
      },
    }
  } catch (e) {
    // Raises when website's CSP disallows blob: protocol, trying to recover
    if ((e as Error)?.name === "SecurityError") {
      console.warn(
        "[LIQA] Warning: Content Security Policy disallows blob: protocol for WebWorkers. For effective LIQA work, please follow the instructions on https://docs.liqa.haut.ai/going-live.html#set-up-csp-for-using-liqa. For any questions, contact the Haut.AI support team (https://docs.liqa.haut.ai/support-desk.html#reaching-for-the-support-team).",
      )
    }

    const model = await import("./illumination.model").then(({ create }) =>
      create(deps, { onProgress }),
    )

    onReady(deps)

    return {
      predict: (frame: FramePixels, landmarks: Landmarks) =>
        model.predict(frameToInput(frame, landmarks)),
      destroy: () => model.destroy(),
    }
  }
}
